import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './PriceCalendar.css';
import {
  calendarHasAnyDiscountedDay,
  getCalendarPriceForDate,
  getDynamicPricingCoverageForDate
} from '../../utils/venueCalendarPricing';
import { formatLocalCalendarYmd, getYmdInTimezone } from '../VenueDashboard/utils/dateUtils';
import { FigmaChevronDownIcon } from '../icons';

// SVG Icons as components
const OffersIcon = () => (
  <img src="/manual-booking/icons/discount-tag.png" alt="" width="18" height="18" />
);

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SmallOffersIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.3639 3.63604C20.0518 5.32387 21 7.61305 21 10Z" fill="#5a9327"/>
    <circle cx="12" cy="11" r="1.5" fill="white"/>
  </svg>
);

const PriceCalendar = ({ 
  selectedDate, 
  onDateSelect, 
  venue, 
  dynamicPricing = [],
  dynamicPricingPerPerson = [],
  defaultPerPersonPrice = null,
  flatPricing = [],
  minBookingHours = 2,
  /** 'exclusive' (full venue) | 'per_person' */
  bookingMode = 'exclusive',
  showDiscountedRatesBanner = true,
  dropdownBelow = false,
  hidePrice = false,
  /** Set of YYYY-MM-DD with no valid booking (start + min duration end) for full venue or per person. */
  fullyUnavailableDatesYmd = null,
  onVisibleMonthChange = null,
  /** Menu matches the field width and the venue-brief dropdown chrome. */
  matchInputWidth = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date()); // Current month
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [inputRect, setInputRect] = useState(null);
  const inputFieldRef = useRef(null);

  /** Civil "today" and past-date rules follow the venue clock, not the booker's browser (e.g. Australia vs UK). */
  const venueTimezone = venue?.timezone || venue?.time_zone || 'Europe/London';
  const todayVenueYmd = () => getYmdInTimezone(new Date(), venueTimezone);

  // Baseline full-venue hourly rate (fallback for display when API omits rate)
  const pricingRulesForCoverage =
    bookingMode === 'per_person' ? dynamicPricingPerPerson : dynamicPricing;

  const calendarPricingOptions = {
    bookingMode,
    venue,
    dynamicPricing,
    dynamicPricingPerPerson,
    defaultPerPersonPrice,
    flatPricing,
    minBookingHours
  };

  // Price shown per calendar day (mode-specific: per-person vs full-venue priority).
  const getPriceForDate = (date) =>
    getCalendarPriceForDate(date, calendarPricingOptions);

  const generateCalendarDates = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get first day of month
    const firstDay = new Date(year, month, 1);
    
    // Start calendar from the Sunday of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const dates = [];
    
    // Generate exactly 42 days (6 weeks × 7 days)
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth() && 
           date.getFullYear() === currentMonth.getFullYear();
  };

  // Dynamic rule hour coverage for parent (discount copy under calendar).
  const getDynamicPricingCoverage = (date) =>
    getDynamicPricingCoverageForDate(date, pricingRulesForCoverage);

  // "Today" = same calendar day as now() in the venue timezone
  const isToday = (date) => {
    if (!date) return false;
    return formatLocalCalendarYmd(date) === todayVenueYmd();
  };

  // Check if date is selected (local civil YMD — avoids UTC off-by-one)
  const isSelected = (date) => {
    if (!selectedDate || !date) return false;
    const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return selectedDate === ymd;
  };

  // Selectable if the cell's YMD is not before "today" in the venue timezone
  const isAvailable = (date) => {
    if (!date) return false;
    const cellYmd = formatLocalCalendarYmd(date);
    return cellYmd >= todayVenueYmd();
  };

  // Format price for display
  const formatPrice = (priceInfo) => {
    const price = typeof priceInfo === 'object' ? priceInfo?.price : priceInfo;
    const rounded = Number(price);
    const label = Number.isFinite(rounded) ? Math.round(rounded * 100) / 100 : price;
    return `£${label}`;
  };


  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Get month name
  const getMonthName = () => {
    return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Format selected date for display (e.g., "7 Oct 2025")
  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    // Parse ISO date (YYYY-MM-DD) correctly without timezone issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${monthName} ${year}`;
  };

  // Viewport coordinates so the menu stays with the field instead of scrolling with the page.
  const placeDropdown = useCallback((rect) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dropdownWidth = matchInputWidth ? rect.width : Math.min(390, viewportWidth - 32);
    const dropdownHeight = 450;
    const gap = matchInputWidth ? 4 : 8;

    let left;
    if (matchInputWidth) {
      left = rect.left;
    } else if (viewportWidth < 768) {
      left = Math.max(16, (viewportWidth - dropdownWidth) / 2);
    } else {
      left = rect.left - 50;
      if (left + dropdownWidth > viewportWidth - 16) {
        left = viewportWidth - dropdownWidth - 16;
      }
      if (left < 16) {
        left = 16;
      }
    }

    if (matchInputWidth) {
      const inner = dropdownWidth - 16;
      setCellWidth(`${(inner - 48) / 7}px`);
    }

    const spaceBelow = viewportHeight - rect.bottom;
    const shouldFlipAbove = dropdownBelow
      ? spaceBelow < 200
      : rect.bottom + dropdownHeight > viewportHeight;

    const next = shouldFlipAbove
      ? {
        placeAbove: true,
        top: 'auto',
        bottom: viewportHeight - rect.top + 4,
        left,
        width: dropdownWidth,
        maxHeight: Math.max(160, rect.top - 20)
      }
      : {
        placeAbove: false,
        top: rect.bottom + gap,
        bottom: 'auto',
        left,
        width: dropdownWidth,
        maxHeight: null
      };

    setInputRect((prev) => {
      if (
        prev &&
        prev.placeAbove === next.placeAbove &&
        prev.top === next.top &&
        prev.bottom === next.bottom &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.maxHeight === next.maxHeight
      ) {
        return prev;
      }
      return next;
    });
  }, [dropdownBelow, matchInputWidth]);

  // Toggle calendar visibility
  const toggleCalendar = (e) => {
    if (!isCalendarOpen) {
      placeDropdown(e.currentTarget.getBoundingClientRect());
    }
    setIsCalendarOpen(!isCalendarOpen);
  };

  useEffect(() => {
    if (!isCalendarOpen) return undefined;
    const syncPosition = () => {
      if (!inputFieldRef.current) return;
      placeDropdown(inputFieldRef.current.getBoundingClientRect());
    };
    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [isCalendarOpen, placeDropdown]);

  // Handle date selection - use local date to avoid timezone off-by-one
  const handleDateSelect = (date) => {
    const dateStr = typeof date === 'string'
      ? date
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const [y, m, d] = dateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    const coverage = getDynamicPricingCoverage(dateObj);
    onDateSelect(dateStr, coverage);
    setIsCalendarOpen(false); // Close calendar after selection
  };

  // Close calendar only on click outside or Escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCalendarOpen && 
          !event.target.closest('.price-calendar-wrapper') && 
          !event.target.closest('.calendar-dropdown')) {
        setIsCalendarOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (isCalendarOpen && event.key === 'Escape') {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCalendarOpen]);

  useEffect(() => {
    if (typeof onVisibleMonthChange !== 'function') return;
    onVisibleMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
  }, [currentMonth, onVisibleMonthChange]);

  const dates = generateCalendarDates();
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate cell width based on viewport
  const [cellWidth, setCellWidth] = useState('52.286px');
  
  useEffect(() => {
    if (matchInputWidth) return undefined;
    const updateCellWidth = () => {
      const viewportWidth = window.innerWidth;
      if (viewportWidth < 480) {
        // Small mobile - scale down
        const availableWidth = Math.min(viewportWidth - 64, 320); // 64px for padding/margins
        const calculatedWidth = (availableWidth - 48) / 7; // 48px for gaps (8px * 6)
        setCellWidth(`${Math.max(calculatedWidth, 36)}px`);
      } else {
        setCellWidth('52.286px');
      }
    };
    
    updateCellWidth();
    window.addEventListener('resize', updateCellWidth);
    return () => window.removeEventListener('resize', updateCellWidth);
  }, [matchInputWidth]);

  return (
    <div className="price-calendar-wrapper">
      {/* Discounted Rates Banner */}
      {showDiscountedRatesBanner &&
        calendarHasAnyDiscountedDay({
          bookingMode,
          venue,
          dynamicPricing,
          dynamicPricingPerPerson,
          defaultPerPersonPrice
        }) && (
        <div className="booking-discount-banner">
          <OffersIcon />
          <p>Discounted rates for select dates</p>
        </div>
      )}

      {/* Date Input Field - Always Visible */}
      <div 
        ref={inputFieldRef}
        className="date-input-field"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleCalendar(e);
        }}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: '1px solid #dadbe1',
          borderRadius: '8px',
          background: '#fafafa',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '16px',
          fontWeight: '500',
          color: selectedDate ? '#000000' : '#9ca3af',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ flex: 1 }}>
          {selectedDate ? formatSelectedDate() : 'Select date'}
        </span>
        <span
          className={`date-input-chevron${isCalendarOpen ? ' date-input-chevron--open' : ''}`}
        >
          <FigmaChevronDownIcon />
        </span>
      </div>




      {/* Calendar Dropdown - Rendered as Portal to avoid clipping */}
      {isCalendarOpen && inputRect && createPortal(
        <div 
          className={`calendar-dropdown${matchInputWidth ? ' vd-calendar-menu' : ''}`}
          style={{
            position: 'fixed',
            top: inputRect.placeAbove ? 'auto' : `${inputRect.top}px`,
            bottom: inputRect.placeAbove ? `${inputRect.bottom}px` : 'auto',
            left: `${inputRect.left}px`,
            width: `${inputRect.width}px`,
            zIndex: 999999,
            background: 'white',
            border: matchInputWidth ? '1px solid rgba(17, 17, 17, 0.12)' : '1px solid #dadbe1',
            borderRadius: '8px',
            boxShadow: matchInputWidth ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            padding: matchInputWidth ? '8px' : '16px',
            boxSizing: 'border-box',
            maxHeight: inputRect.maxHeight ? `${inputRect.maxHeight}px` : 'calc(100vh - 32px)',
            overflowY: 'auto'
          }}
        >
          {/* Calendar Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            ...(matchInputWidth ? {
              margin: '-8px -8px 0',
              padding: '10px 12px',
              borderBottom: '1px solid rgba(17, 17, 17, 0.08)'
            } : {})
          }}>
            <h3 style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: matchInputWidth || window.innerWidth < 480 ? '16px' : '20px',
              fontWeight: '500',
              color: '#111111',
              margin: 0,
              lineHeight: '1.5'
            }}>
              {getMonthName()}
            </h3>
            
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center'
            }}>
            <button 
              onClick={goToPreviousMonth}
              aria-label="Previous month"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#000000'
                }}
              >
                <ChevronLeftIcon />
            </button>
            
            <button 
              onClick={goToNextMonth}
              aria-label="Next month"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#000000'
                }}
              >
                <ChevronRightIcon />
            </button>
            </div>
          </div>

          {matchInputWidth ? null : (
          <div style={{
            height: '1px',
            background: '#dadbe1',
            opacity: '0.2',
            marginBottom: '8px'
          }} />
          )}

          {/* Weekday Headers */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: matchInputWidth ? '8px' : 0,
            marginBottom: matchInputWidth ? '8px' : 0
          }}>
            {weekdays.map(day => (
              <div 
                key={day} 
                style={{
                  width: cellWidth,
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: window.innerWidth < 480 ? '12px' : '14px',
                  fontWeight: '400',
                  color: 'rgba(17, 17, 17, 0.3)',
                  padding: '0',
                  boxSizing: 'border-box'
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0'
          }}>
            {/* One row per week that contains a day of this month */}
            {Array.from({ length: 6 }).map((_, weekIndex) => {
              const weekDates = dates.slice(weekIndex * 7, (weekIndex + 1) * 7);
              if (!weekDates.some(isCurrentMonth)) return null;
              const isMobile = window.innerWidth < 480;
              return (
              <div 
                key={weekIndex}
            style={{
                  display: 'flex',
                  gap: '8px'
                }}
              >
                {/* 7 days per row */}
                {weekDates.map((date, dayIndex) => {
              const currentMonth = isCurrentMonth(date);
                  if (!currentMonth) {
                    return (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        aria-hidden
                        style={{
                          width: cellWidth,
                          minHeight: isMobile ? '32px' : '38px',
                          boxSizing: 'border-box'
                        }}
                      />
                    );
                  }
              const priceInfo = getPriceForDate(date);
              const available = isAvailable(date);
              const selected = isSelected(date);
              const today = isToday(date);
                  const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const fullyUnavailable = fullyUnavailableDatesYmd?.has?.(ymd);
                  const isPastDate = !available && !currentMonth;
                  
                  // Determine if this date has a discounted price
                  const hasDiscount = priceInfo.isDiscounted;
                  
                  const canSelect = available && currentMonth && !fullyUnavailable;

                  // Base styles
                  let cellStyles = {
                    width: cellWidth,
                    minHeight: isMobile ? '32px' : '38px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: hasDiscount ? '2px' : '4px',
                    padding: isMobile ? '4px 2px' : '6px 4px',
                    boxSizing: 'border-box',
                    cursor: canSelect ? 'pointer' : 'default',
                    borderRadius: '4px',
                    transition: 'background 0.2s ease'
                  };

                  // Apply conditional styles
                  if (isPastDate) {
                    // Empty cell or past date
                    cellStyles = {
                      ...cellStyles,
                      opacity: 0.2
                    };
                  } else if (fullyUnavailable) {
                    cellStyles = {
                      ...cellStyles,
                      opacity: 0.45,
                      cursor: 'not-allowed'
                    };
                  } else if (selected) {
                    // Selected date
                    cellStyles = {
                      ...cellStyles,
                      background: '#046242',
                      color: 'white'
                    };
                  } else if (hasDiscount) {
                    // Discounted date with light green background
                    cellStyles = {
                      ...cellStyles,
                      background: 'rgba(4, 98, 66, 0.1)'
                    };
                  } else if (today) {
                    // Today with green text
                    cellStyles = {
                      ...cellStyles
                    };
                  }

                  return (
                    <div
                      key={`${weekIndex}-${dayIndex}`}
                      title={fullyUnavailable ? 'No availability' : undefined}
                      onClick={() => {
                        if (canSelect) {
                          handleDateSelect(date);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (canSelect && !selected) {
                          e.currentTarget.style.background = 'rgba(4, 98, 66, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (canSelect && !selected) {
                          if (hasDiscount) {
                            e.currentTarget.style.background = 'rgba(4, 98, 66, 0.1)';
                          } else {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }
                      }}
                      style={cellStyles}
                    >
                      {/* Date Number */}
                      <div style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: isMobile ? '11px' : '13px',
                        fontWeight: selected ? '500' : (today ? '700' : '500'),
                        color: selected ? 'white' : (fullyUnavailable ? '#9ca3af' : (today && !hasDiscount ? '#046242' : 'black')),
                        lineHeight: isMobile ? '14px' : '16px',
                        textAlign: 'center'
                      }}>
                        {date.getDate()}
                      </div>
                      
                      {/* Price Display - hidden when admin/API hides pricing; grey days still show indicative rates */}
                      {!hidePrice && currentMonth && available ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: isMobile ? '2px' : '3px'
                        }}>
                          {hasDiscount && !selected && (
                            <div style={{ 
                              width: isMobile ? '9px' : '10px', 
                              height: isMobile ? '9px' : '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <SmallOffersIcon />
                            </div>
                          )}
                          <p style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: isMobile ? '8px' : '9px',
                            fontWeight: '500',
                            lineHeight: '1.2',
                            color: selected ? '#d9fff2' : (hasDiscount ? '#5a9327' : '#7a7a7a'),
                            margin: 0,
                            whiteSpace: 'nowrap'
                          }}>
                            {formatPrice(priceInfo)}
                          </p>
                        </div>
                      ) : (
                        currentMonth && (
                          <p style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: isMobile ? '8px' : '9px',
                            fontWeight: '500',
                            lineHeight: '1.2',
                            color: '#7a7a7a',
                            margin: 0
                          }}>
                            -
                          </p>
                        )
                      )}
                </div>
              );
            })}
              </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PriceCalendar;
